import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowRight, CalendarDays, MapPin } from 'lucide-react';
import { colors, spacing, typography, borderRadius, shadows, breakpoints } from '../styles/theme';
import EventFinderAPI from '../services/api';
import type { TopCity } from '../types/api';
import { formatEventLocalDateTime } from '../utils/eventDateTime';
import { truncate } from '../utils/eventPresentation';
import config from '../config';

/**
 * "Top event cities" cards for the home page.
 *
 * Every card is backed by a curated `locations.json` city, so the label is a
 * single clean line and the card links to that city's canonical events page.
 * Cities outside the curated dataset are omitted by the API rather than being
 * folded into a same-named city.
 */

interface TopCitiesProps {
  limit?: number;
  showTitle?: boolean;
}

const CitiesContainer = styled.section`
  width: 100%;
  margin-bottom: ${spacing[8]};
`;

const SectionTitle = styled.h2`
  font-size: ${typography.fontSizes['2xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[6]};
  text-align: center;

  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes.xl};
    margin-bottom: ${spacing[4]};
  }
`;

const CitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${spacing[4]};
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
    padding: 0 ${spacing[4]};
  }
`;

const CityCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  background: ${colors.white};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[5]};
  box-shadow: ${shadows.sm};
  text-decoration: none;
  color: inherit;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${colors.gradients.primary};
  }

  &:hover,
  &:focus-visible {
    transform: translateY(-3px);
    box-shadow: ${shadows.lg};
    border-color: ${colors.primary[300]};
  }

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }

  @media (max-width: ${breakpoints.sm}) {
    padding: ${spacing[4]};
  }
`;

const CityHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[3]};
`;

/** City, region, and country stay on one line — no empty separators. */
const CityName = styled.h3`
  margin: 0;
  font-size: ${typography.fontSizes.lg};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  line-height: ${typography.lineHeights.tight};
  overflow-wrap: anywhere;

  svg {
    display: inline-block;
    vertical-align: -2px;
    margin-right: ${spacing[1]};
    color: ${colors.primary[500]};
  }
`;

const CountBlock = styled.div`
  flex-shrink: 0;
  text-align: right;
`;

const CountValue = styled.div`
  font-size: ${typography.fontSizes['2xl']};
  font-weight: ${typography.fontWeights.bold};
  color: ${colors.primary[600]};
  line-height: 1.1;
`;

const CountLabel = styled.div`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const HighlightRow = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
`;

const HighlightChip = styled.li`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.full};
  background: ${colors.primary[50]};
  border: 1px solid ${colors.primary[100]};
  color: ${colors.primary[800]};
  font-size: ${typography.fontSizes.xs};

  strong {
    font-weight: ${typography.fontWeights.semibold};
  }
`;

const PartnerLine = styled.p`
  margin: 0;
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
  overflow-wrap: anywhere;

  strong {
    color: ${colors.text.primary};
    font-weight: ${typography.fontWeights.medium};
  }
`;

const NextEvent = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  margin-top: auto;
  padding-top: ${spacing[2]};
  border-top: 1px solid ${colors.border.light};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};

  svg {
    color: ${colors.text.tertiary};
  }

  span:last-child {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    color: ${colors.text.link};
  }
`;

const StateBlock = styled.div`
  text-align: center;
  padding: ${spacing[8]};
  color: ${colors.text.secondary};
`;

const SkeletonGrid = styled(CitiesGrid)``;

const SkeletonCard = styled.div`
  height: 190px;
  border-radius: ${borderRadius.xl};
  background: linear-gradient(90deg, ${colors.gray[100]} 25%, ${colors.gray[200]} 37%, ${colors.gray[100]} 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;

  @keyframes shimmer {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }
`;

const RetryButton = styled.button`
  background: ${colors.primary[500]};
  color: ${colors.white};
  border: none;
  border-radius: ${borderRadius.lg};
  min-height: 44px;
  padding: 0 ${spacing[6]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  cursor: pointer;

  &:hover {
    background: ${colors.primary[600]};
  }

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }
`;

function formatNextEvent(value: string | null): string {
  if (!value) return 'No upcoming events';
  const formatted = formatEventLocalDateTime(value, { includeTime: false });
  return formatted ? `Next: ${formatted}` : 'No upcoming events';
}

function formatPartners(city: TopCity): React.ReactNode {
  if (!city.partners || city.partners.length === 0) return null;
  // Some entity names carry an appended industry classification, which would
  // otherwise dominate the card.
  const names = city.partners.map((p) => truncate(p.displayName, 32));
  const shown = names.slice(0, 2).join(', ');
  const remainder = names.length - Math.min(names.length, 2);
  return (
    <PartnerLine title={city.partners.map((p) => p.displayName).join(', ')}>
      Hosted by <strong>{shown}</strong>
      {remainder > 0 ? ` +${remainder} more` : ''}
    </PartnerLine>
  );
}

export const TopCities: React.FC<TopCitiesProps> = ({
  limit = config.pagination.topCitiesLimit,
  showTitle = true,
}) => {
  const [cities, setCities] = useState<TopCity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    setIsLoading(true);
    setError(null);
    setCities([]);

    (async () => {
      try {
        const result = await EventFinderAPI.getTopCities(limit, controller.signal);
        if (cancelled) return;
        setCities(result);
      } catch (err) {
        if (cancelled || (err as Error)?.name === 'AbortError') return;
        console.error('Error fetching top cities:', err);
        setError('Failed to load cities. Please try again.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [limit, reloadToken]);

  const handleRetry = useCallback(() => setReloadToken((token) => token + 1), []);

  if (isLoading) {
    return (
      <CitiesContainer>
        {showTitle && <SectionTitle>Top Event Cities</SectionTitle>}
        <SkeletonGrid aria-busy="true" aria-label="Loading cities">
          {Array.from({ length: Math.min(limit, 4) }, (_, i) => (
            <SkeletonCard key={i} />
          ))}
        </SkeletonGrid>
      </CitiesContainer>
    );
  }

  if (error) {
    return (
      <CitiesContainer>
        {showTitle && <SectionTitle>Top Event Cities</SectionTitle>}
        <StateBlock role="alert">
          <p>{error}</p>
          <RetryButton type="button" onClick={handleRetry}>
            Try again
          </RetryButton>
        </StateBlock>
      </CitiesContainer>
    );
  }

  if (cities.length === 0) {
    return (
      <CitiesContainer>
        {showTitle && <SectionTitle>Top Event Cities</SectionTitle>}
        <StateBlock role="status">No cities with upcoming events yet.</StateBlock>
      </CitiesContainer>
    );
  }

  return (
    <CitiesContainer>
      {showTitle && <SectionTitle>Top Event Cities</SectionTitle>}
      <CitiesGrid>
        {cities.map((city) => (
          <CityCard
            key={city.slug}
            to={city.canonicalUrl}
            aria-label={`${city.displayName}: ${city.upcomingEvents} upcoming events`}
          >
            <CityHeader>
              <CityName>
                <MapPin aria-hidden="true" size={16} />
                {city.displayName}
              </CityName>
              <CountBlock>
                <CountValue>{city.upcomingEvents.toLocaleString()}</CountValue>
                <CountLabel>Upcoming</CountLabel>
              </CountBlock>
            </CityHeader>

            {city.highlights.length > 0 && (
              <HighlightRow>
                {city.highlights.map((highlight) => (
                  <HighlightChip key={highlight.key}>
                    <strong>{highlight.count}</strong>
                    {highlight.label}
                  </HighlightChip>
                ))}
              </HighlightRow>
            )}

            {formatPartners(city)}

            <NextEvent>
              <CalendarDays aria-hidden="true" size={14} />
              <span>{formatNextEvent(city.nextEventDate)}</span>
              <span aria-hidden="true">
                View
                <ArrowRight size={14} />
              </span>
            </NextEvent>
          </CityCard>
        ))}
      </CitiesGrid>
    </CitiesContainer>
  );
};

export default TopCities;
